// A tooltip (WCAG 1.4.13): shows on hover and on focus, Escape hides it without moving focus, and the pointer can
// move onto the bubble without it closing. The bubble is portalled into the nearest layer (inline under SSR and
// before mount) and is always in the DOM, hidden when closed, so a describedby reference resolves at rest.
// "describe" (default): the bubble carries its own text and the trigger is aria-describedby it.
// "mirror": the text repeats what the trigger already exposes (an icon button's aria-label, a blocked control's
// inline .mc-sr reason), so the bubble is aria-hidden and linked to nothing.
// The bubble sits above the trigger (top), under it (bottom), or beside it on its inline end (end: the navigation's icon
// rail, UAT F-146, where a bubble above an item would cover the item over it).

import { cloneElement, useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { cx } from "./cx";
import { McPortal, usePortalTarget } from "./McRoot";
import { useIsoLayoutEffect } from "./useIsoLayoutEffect";
import { useSvgId } from "./useSvgId";
import { vars } from "./vars";

export interface TooltipProps {
  /** The bubble's text, from a labels module. */
  label: string;
  /** The trigger: one element. */
  children: ReactElement<{ "aria-describedby"?: string | undefined }>;
  mode?: "describe" | "mirror" | undefined;
  placement?: "top" | "bottom" | "end" | undefined;
}

/** Time the pointer has to cross from the trigger to the bubble (and back) before the bubble closes. */
const GRACE_MS = 120;
/** The bubble's least distance from the viewport's edges. */
const EDGE = 8;

export function Tooltip({ label, children, mode = "describe", placement = "top" }: TooltipProps) {
  const id = `${useSvgId()}-tip`;
  const target = usePortalTarget();
  const anchor = useRef<HTMLSpanElement | null>(null);
  const bubble = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const show = useCallback(() => { cancel(); setOpen(true); }, [cancel]);
  const hideSoon = useCallback(() => {
    cancel();
    timer.current = setTimeout(() => { timer.current = null; setOpen(false); }, GRACE_MS);
  }, [cancel]);
  useEffect(() => cancel, [cancel]);

  // An open tooltip takes Escape first and keeps it: the listener is on window in the capture phase, ahead of a
  // Dialog's or SlideOver's focus trap (document, capture), so the first Escape dismisses only the bubble (WCAG
  // 1.4.13) and the surface closes on a second.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      cancel();
      setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, cancel]);

  // Centred on the trigger, kept EDGE px inside the viewport, measured from the layer's origin; at the end, from the
  // trigger's end edge and its middle.
  useIsoLayoutEffect(() => {
    if (!open || !target || !anchor.current) return;
    const rect = anchor.current.getBoundingClientRect();
    const layer = target.getBoundingClientRect();
    if (placement === "end") {
      setAt({ x: Math.round(rect.right - layer.left), y: Math.round(rect.top + rect.height / 2 - layer.top) });
      return;
    }
    const half = (bubble.current?.getBoundingClientRect().width ?? 0) / 2;
    const viewport = document.documentElement.clientWidth;
    const centre = Math.min(Math.max(rect.left + rect.width / 2, half + EDGE), Math.max(half + EDGE, viewport - half - EDGE));
    setAt({ x: Math.round(centre - layer.left), y: Math.round((placement === "top" ? rect.top : rect.bottom) - layer.top) });
  }, [open, target, placement]);

  const describe = mode === "describe";
  const trigger = describe
    ? cloneElement(children, { "aria-describedby": [children.props["aria-describedby"], id].filter(Boolean).join(" ") })
    : children;
  const floating = target !== null && at !== null;

  return (
    <span
      className="mc-tip-anchor"
      ref={anchor}
      onPointerEnter={show}
      onPointerLeave={hideSoon}
      onFocus={show}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) { cancel(); setOpen(false); } }}
    >
      {trigger}
      <McPortal>
        <span
          ref={bubble}
          role="tooltip"
          id={describe ? id : undefined}
          aria-hidden={describe ? undefined : true}
          className={cx("mc-tip", placement === "bottom" && "mc-tip-below", placement === "end" && "mc-tip-end", floating && "mc-tip-float")}
          style={floating ? vars({ "--mc-tip-x": at.x, "--mc-tip-y": at.y }) : undefined}
          hidden={!open}
          onPointerEnter={show}
          onPointerLeave={hideSoon}
        >
          {label}
        </span>
      </McPortal>
    </span>
  );
}
